import { authenticatedFetch } from "./apiClient";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const WP_BASE_PATH = "/fn/integrations/wordpress";
const WP_ACTIONS_BASE_PATH = "/v1/integrations/wordpress";

export interface WordPressActionResponse {
  message: string;
  status: string;
  page_id?: string;
  version_id?: string;
  job_id?: string;
  scheduled_at?: string;
  implemented_page_id?: string | null;
}

export interface WordPressActionConflictDetail {
  message?: string;
  implemented_page_id?: string | null;
}

export class WordPressIdeaConflictError extends Error {
  implementedPageId: string | null;

  constructor(message: string, implementedPageId?: string | null) {
    super(message);
    this.name = "WordPressIdeaConflictError";
    this.implementedPageId = implementedPageId ?? null;
  }
}

export interface WordPressDiscoverResponse {
  site_url: string;
  site_name: string;
  rest_api_available: boolean;
  error?: string;
}

export interface WordPressConnectResponse {
  ok?: boolean;
  site_id?: string;
  site_name?: string;
  site_url?: string;
  error?: string;
}

export class WordPressService {
  static async discoverSite(
    siteUrl: string,
  ): Promise<WordPressDiscoverResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_BASE_PATH}/discover`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ site_url: siteUrl }),
      },
      // Read-only probe of a public site: it writes nothing, so viewers may run it.
      { isViewerAllowed: true },
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || `Discovery failed (${response.status})`);
    }

    return response.json();
  }

  static async connectWithAppPassword(
    siteUrl: string,
    username: string,
    appPassword: string,
  ): Promise<WordPressConnectResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_BASE_PATH}/app-password`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          site_url: siteUrl,
          username,
          app_password: appPassword,
        }),
      },
    );

    return response.json();
  }

  static async syncWordPress(
    domainId: string,
    siteId: string,
  ): Promise<WordPressActionResponse> {
    const params = new URLSearchParams({ domain_id: domainId, site_id: siteId });
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/sync?${params}`,
      { method: "POST" },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Sync failed (${response.status})`);
    }
    return response.json();
  }

  static async createPost(
    domainId: string,
    keyword: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._createContent(domainId, keyword, "posts", customInstructions);
  }

  static async createPage(
    domainId: string,
    keyword: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._createContent(domainId, keyword, "pages", customInstructions);
  }

  static async createPostFromIdea(
    domainId: string,
    ideaId: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._createContentFromIdea(
      domainId,
      ideaId,
      "posts",
      customInstructions,
    );
  }

  static async createPageFromIdea(
    domainId: string,
    ideaId: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._createContentFromIdea(
      domainId,
      ideaId,
      "pages",
      customInstructions,
    );
  }

  private static async _createContent(
    domainId: string,
    keyword: string,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/${wpType}/create`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain_id: domainId, keyword, custom_instructions: customInstructions }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Create failed (${response.status})`);
    }
    return response.json();
  }

  private static async _createContentFromIdea(
    domainId: string,
    ideaId: string,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/${wpType}/create-from-idea`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain_id: domainId,
          idea_id: ideaId,
          custom_instructions: customInstructions,
        }),
      },
    );


    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const detail = WordPressService._extractErrorDetail(data);

      if (response.status === 409) {
        throw new WordPressIdeaConflictError(
          detail.message || "Idea already implemented",
          detail.implemented_page_id,
        );
      }

      throw new Error(detail.message || `Create failed (${response.status})`);
    }

    return response.json();
  }

  static async editPost(
    domainId: string,
    pageId: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._editContent(domainId, pageId, "posts", customInstructions);
  }

  static async editPage(
    domainId: string,
    pageId: string,
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    return WordPressService._editContent(domainId, pageId, "pages", customInstructions);
  }

  private static async _editContent(
    domainId: string,
    pageId: string,
    wpType: "posts" | "pages",
    customInstructions?: string,
  ): Promise<WordPressActionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/${wpType}/${pageId}/edit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain_id: domainId, custom_instructions: customInstructions }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Edit failed (${response.status})`);
    }
    return response.json();
  }

  static async saveManualVersion(
    domainId: string,
    pageId: string,
    wpType: "posts" | "pages",
    edits: {
      wp_title?: string | null;
      wp_slug?: string | null;
      wp_excerpt?: string | null;
      wp_content?: string | null;
      wp_categories?: number[] | null;
    },
  ): Promise<WordPressActionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/${wpType}/${pageId}/manual-edit`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain_id: domainId,
          wp_title: edits.wp_title ?? null,
          wp_slug: edits.wp_slug ?? null,
          wp_excerpt: edits.wp_excerpt ?? null,
          wp_content: edits.wp_content ?? null,
          wp_categories: edits.wp_categories ?? null,
        }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Manual edit save failed (${response.status})`);
    }
    return response.json();
  }

  static async approveVersion(
    domainId: string,
    pageId: string,
    versionId: string,
    wpType: "posts" | "pages",
  ): Promise<WordPressActionResponse> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${WP_ACTIONS_BASE_PATH}/${wpType}/${pageId}/versions/${versionId}/approve`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ domain_id: domainId }),
      },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || `Approve failed (${response.status})`);
    }
    return response.json();
  }

  private static _extractErrorDetail(data: unknown): WordPressActionConflictDetail {
    if (!data || typeof data !== "object") {
      return {};
    }

    const detail =
      "detail" in data && typeof data.detail === "object" && data.detail !== null
        ? data.detail
        : data;

    return {
      message:
        "message" in detail && typeof detail.message === "string"
          ? detail.message
          : undefined,
      implemented_page_id:
        "implemented_page_id" in detail &&
        (typeof detail.implemented_page_id === "string" ||
          detail.implemented_page_id === null)
          ? detail.implemented_page_id
          : undefined,
    };
  }
}
